import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { takeWhile } from 'rxjs/operators';

import Card from 'react-bootstrap/Card';
import '../../../css/card.css';

import { getDensityVerbiageByFacility, updateDensityLetterVerbiage, deleteDensityLetterVerbiage } from '../../../common/services/DensityLetterVerbiageService';

import { userAssociatedFacilities } from '../../../common/services/ApiService';

import { toast, confirm } from '@rickylandino/react-messages';
import { Select, Table, Input, Button, Spin } from 'antd';
import { UndoOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;

class ManageDensityWithVerbiage extends Component {
    constructor(props) {
        super(props);

        this.state = {
            densityVerbiageList: [],
            loaded: false,
            userAssociatedFacilities: [],
            formFields: {
                facilityID: ''
            },
            editingKey: ''
        }

        this.alive = false;
    }

    componentDidMount() {
        this.alive = true;

        userAssociatedFacilities.pipe(takeWhile(() => this.alive)).subscribe((af) => {
            if (af?.length > 0) {
                let defaultFacility = af.find(f => f.defaultFacility === 1)?.facilityModel?.facilityID || af[0].facilityModel?.facilityID;

                this.setState({
                    userAssociatedFacilities: af,
                    formFields: {
                        facilityID: this.state.formFields.facilityID === '' ? defaultFacility : this.state.formFields.facilityID
                    }
                }, () => {
                    this.loadDensityVerbiage(defaultFacility);
                });
            }
        });
    }

    componentWillUnmount() {
        this.alive = false;
    }

    loadDensityVerbiage = (facilityID) => {
        getDensityVerbiageByFacility(facilityID).then(data => {
            if (data) {
                // Mark items with custom verbiage as dirty for tracking
                const densityList = data.map(item => ({
                    ...item,
                    isDirty: false,
                    originalCustomVerbiage: item.customVerbiage,
                    key: item.breastDensityID
                }));

                this.setState({
                    densityVerbiageList: densityList,
                    loaded: true
                });
            }
        }).catch(error => {
            toast.error("Failed to load density verbiage");
            console.error(error);
        });
    }

    handleFacilityChange = (selectedFacilityID) => {
        this.setState({ 
            formFields: { ...this.state.formFields, facilityID: selectedFacilityID },
            loaded: false
        }, () => {
            this.loadDensityVerbiage(selectedFacilityID);
        });
    }

    handleVerbiageChange = (value, breastDensityID) => {
        const densityVerbiageList = [...this.state.densityVerbiageList];
        const idx = densityVerbiageList.findIndex(item => item.breastDensityID === breastDensityID);

        if (idx !== -1) {
            densityVerbiageList[idx].customVerbiage = value;
            densityVerbiageList[idx].isDirty = true;

            this.setState({ densityVerbiageList });
        }
    }

    handleSubmit = () => {
        const itemsToUpdate = this.state.densityVerbiageList.filter(i => i.isDirty);

        if (itemsToUpdate.length === 0) {
            toast.info("No changes to save");
            return;
        }

        const promises = itemsToUpdate.map(item => {
            const payload = {
                breastDensityID: item.breastDensityID,
                facilityID: this.state.formFields.facilityID,
                verbiage: item.customVerbiage || null
            };

            // If custom verbiage is empty/null and there was a previous custom verbiage, delete it
            if (!item.customVerbiage && item.densityLetterVerbiageID) {
                return deleteDensityLetterVerbiage(this.state.formFields.facilityID, item.breastDensityID);
            }
            // If there's a densityLetterVerbiageID, update it
            else if (item.densityLetterVerbiageID) {
                payload.densityLetterVerbiageID = item.densityLetterVerbiageID;
                return updateDensityLetterVerbiage(payload);
            }
            // Otherwise, create a new one (POST will be handled by update endpoint if needed)
            else {
                return updateDensityLetterVerbiage(payload);
            }
        });

        Promise.all(promises)
            .then(() => {
                toast.success("Density verbiage has been updated");
                // Reload the data to get fresh state
                this.loadDensityVerbiage(this.state.formFields.facilityID);
            })
            .catch(error => {
                toast.error("Failed to update density verbiage");
                console.error(error);
            });
    }

    resetToDefault = (row) => {
        const facilityName = this.state.userAssociatedFacilities.find(
            f => f.facilityModel.facilityID === this.state.formFields.facilityID
        )?.facilityModel?.facilityName;

        confirm({
            title: `Reset "${row.densityDescription}" to default verbiage?`,
            content: `This will remove the custom verbiage for ${facilityName} and use the default verbiage.`,
            buttons: ["Yes", "No"],
            theme: window.sessionStorage.getItem("theme") === 'dark' ? 'dark' : 'light'
        }, (buttonPressed) => {
            if (buttonPressed === 'Yes') {
                if (row.densityLetterVerbiageID) {
                    deleteDensityLetterVerbiage(this.state.formFields.facilityID, row.breastDensityID)
                        .then(() => {
                            toast.success("Custom verbiage removed. Using default verbiage.");
                            this.loadDensityVerbiage(this.state.formFields.facilityID);
                        })
                        .catch(error => {
                            toast.error("Failed to reset verbiage");
                            console.error(error);
                        });
                } else {
                    // Just clear the field locally if no custom verbiage exists
                    const densityVerbiageList = [...this.state.densityVerbiageList];
                    const idx = densityVerbiageList.findIndex(item => item.breastDensityID === row.breastDensityID);
                    
                    if (idx !== -1) {
                        densityVerbiageList[idx].customVerbiage = null;
                        densityVerbiageList[idx].isDirty = false;
                        this.setState({ densityVerbiageList });
                    }
                }
            }
            return 0;
        });
    }

    render() {
        let { Option } = Select;

        const columns = [
            {
                title: 'Density Type',
                dataIndex: 'densityDescription',
                key: 'densityDescription',
                width: 150,
                fixed: 'left'
            },
            {
                title: 'Default Verbiage',
                dataIndex: 'defaultVerbiage',
                key: 'defaultVerbiage',
                width: '35%',
                render: (text) => (
                    <TextArea
                        value={text || ''}
                        readOnly
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        style={{ 
                            backgroundColor: '#f8f9fa',
                            cursor: 'not-allowed'
                        }}
                    />
                )
            },
            {
                title: 'Custom Verbiage (Optional)',
                dataIndex: 'customVerbiage',
                key: 'customVerbiage',
                width: '35%',
                render: (text, record) => {
                    const isCustom = text !== null && text !== undefined && text !== '';
                    return (
                        <div>
                            <TextArea
                                value={text || ''}
                                onChange={(e) => this.handleVerbiageChange(e.target.value, record.breastDensityID)}
                                autoSize={{ minRows: 3, maxRows: 6 }}
                                placeholder="Enter custom verbiage or leave blank to use default"
                                style={{ 
                                    backgroundColor: isCustom ? '#fff3cd' : 'transparent',
                                    border: isCustom ? '1px solid #ffc107' : '1px solid #d9d9d9'
                                }}
                            />
                            {isCustom && (
                                <small className="text-muted" style={{ fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
                                    Custom verbiage (overrides default)
                                </small>
                            )}
                        </div>
                    );
                }
            },
            {
                title: 'Actions',
                key: 'action',
                width: 100,
                align: 'center',
                render: (text, record) => {
                    const hasCustom = record.customVerbiage !== null && 
                                     record.customVerbiage !== undefined && 
                                     record.customVerbiage !== '';
                    return hasCustom ? (
                        <Button
                            type="link"
                            icon={<UndoOutlined />}
                            onClick={() => this.resetToDefault(record)}
                            title="Reset to default verbiage"
                            className="color-pink"
                        />
                    ) : null;
                }
            }
        ];

        return (
            <Fragment>
                {this.state.loaded ? (
                    <Fragment>
                        <div className="row">
                            <div className="form-group col-lg-4">
                                <label className="form-label">Selected Facility</label>
                                <Select
                                    allowClear
                                    placeholder="Please select"
                                    onChange={this.handleFacilityChange}
                                    className="form-control-custom w-100"
                                    bordered={false}
                                    value={this.state.formFields.facilityID || ''}
                                    showSearch
                                    filterOption={(input, option) => 
                                        (option.children[0] + option.children[1]).toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {this.state.userAssociatedFacilities.map((fac, idx) => 
                                        <Option key={fac.facilityModel.facilityID} value={fac.facilityModel.facilityID}>
                                            {fac.facilityModel.facilityNumber && fac.facilityModel.facilityNumber + " - "}
                                            {fac.facilityModel.facilityName}
                                        </Option>
                                    )}
                                </Select>
                            </div>
                        </div>
                        <Card className="pinkCard">
                            <Card.Header>
                                <span>Density Letter Verbiage Management</span>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-3">
                                    <p className="text-muted">
                                        <InfoCircleOutlined className="color-pink" /> Click on the "Custom Verbiage" field to edit. 
                                        Leave blank to use the default verbiage. Custom verbiage will be highlighted in yellow.
                                    </p>
                                </div>
                                <Table
                                    columns={columns}
                                    dataSource={this.state.densityVerbiageList}
                                    pagination={false}
                                    bordered
                                    size="small"
                                    scroll={{ x: 'max-content' }}
                                />
                            </Card.Body>
                        </Card>
                        <button className="btn btn-submit mt-3" onClick={this.handleSubmit}>Save Changes</button>
                    </Fragment>
                ) : (
                    <div className="text-center p-5">
                        <Spin size="large" tip="Loading density verbiage..." />
                    </div>
                )}
            </Fragment>
        );
    }
}

export default withRouter(ManageDensityWithVerbiage);
